import React from 'react';

/**
 * Renders the inner content for object-style visuals (rich_text, image, shape).
 * Called from inside <FreeVisual> children. Used in 4 zones across the builder
 * (report_header, page_header, page_footer, report_footer) with minor styling tweaks.
 *
 * Props:
 *   v         — the visual object (with vizType, textContent, imageUrl, etc.)
 *   onUpdate  — (patch) => void  setter
 *   variant   — 'header' | 'footer' | 'page-header' | 'page-footer' | 'report-header' | 'report-footer'
 *               (only affects placeholder text + default font size)
 */
const VARIANT_CFG = {
  'report-header':  { textSize: 14, placeholder: 'Report title…',  shapeMode: 'block' },
  'report-footer':  { textSize: 10, placeholder: 'Report footer…', shapeMode: 'line' },
  'page-header':    { textSize: 14, placeholder: 'Header text…',   shapeMode: 'line' },
  'page-footer':    { textSize: 9,  placeholder: 'Footer text…',   shapeMode: 'line' },
  'header':         { textSize: 14, placeholder: 'Header text…',   shapeMode: 'block' },
  'footer':         { textSize: 9,  placeholder: 'Footer text…',   shapeMode: 'line' },
};

export function ObjectVisualContent({ v, onUpdate, variant = 'page-header', vData = [], allFields = [], formatValue }) {
  const cfg = VARIANT_CFG[variant] || VARIANT_CFG['page-header'];

  if (v.vizType === 'rich_text') {
    return (
      <div className="w-full h-full p-0 overflow-hidden" onClick={e => e.stopPropagation()}>
        <textarea
          value={v.textContent || ''}
          onChange={e => onUpdate({ textContent: e.target.value })}
          className="w-full h-full bg-transparent border-none outline-none resize-none p-0 m-0"
          style={{
            fontSize: v.textSize || cfg.textSize,
            fontWeight: v.textBold ? 'bold' : 'normal',
            color: v.textColor || 'inherit',
            textAlign: v.textAlign || 'left',
            lineHeight: 1.2,
          }}
          placeholder={cfg.placeholder}
        />
      </div>
    );
  }

  if (v.vizType === 'image') {
    return (
      <div className="w-full h-full flex items-center justify-center">
        {v.imageUrl
          ? <img src={v.imageUrl} alt="" className="max-w-full max-h-full object-contain" />
          : <div className="w-full h-full rounded border-2 border-dashed border-sky-300/30 flex items-center justify-center text-[8px] text-sky-400/40">Image</div>}
      </div>
    );
  }

  if (v.vizType === 'kpi' && vData.length > 0) {
    const measure = v.valueMeasures?.[0];
    const fieldDesc = allFields.find(f => f.name === measure)?.description || measure;
    const value = vData[0]?.[measure];
    return (
      <div className="flex items-center gap-2 h-full">
        <span className="text-[9px] text-muted-foreground">{fieldDesc}</span>
        <span className="text-sm font-bold text-foreground">{formatValue ? formatValue(value) : value}</span>
      </div>
    );
  }

  if (v.vizType === 'shape') {
    if (cfg.shapeMode === 'line') {
      return <div className="w-full" style={{ height: 1, background: v.shapeFill || '#666', marginTop: '50%' }} />;
    }
    // 'block' — rectangle filling parent
    return (
      <div
        className="w-full h-full"
        style={{
          background: v.shapeFill || '#3b82f6',
          opacity: v.shapeOpacity ?? 1,
          borderRadius: v.shapeBorderRadius ?? 0,
          border: v.shapeBorder ? `1px solid ${v.shapeBorder}` : 'none',
        }}
      />
    );
  }

  // Fallback for non-object types — should never reach here
  return <div className="text-[8px] text-muted-foreground/70 h-full flex items-center">{v.title || v.vizType}</div>;
}
